import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  Col,
  Container,
  Row,
  Button,
  FormGroup,
  Input,
  FormFeedback,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { ReactstrapInput } from "reactstrap-formik";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import * as Yup from "yup";
import moment from "moment";

import FormHelpers from "../../helpers/FormHelpers";
import ScrollToFieldError from "./Validation/ScrollToFieldError";
import InfoTooltip from "../UI/Progress/InfoTooltip";
import OptGroupSelectDropDown from "./Fields/OptGroupSelectDropDown";
import DebugOutput from "../DebugOutput";

import { configuratorActions } from "../../store/configurator-slice";
import { cardPreviewActions } from "../../store/card-preview-slice";
import { sendCartData } from "../../store/configurator-actions";

function RequestForm() {
  const dispatch = useDispatch();

  const addingToCart = useSelector((state) => state.configurator.AddingToCart);
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  const requestDetails = currentRequest.DonorRequest;

  const currentProductState =
    currentRequest.Products[currentRequest.CurrentProduct];
  const currentPreviewState = useSelector((state) => state.cardPreview);

  const isUpdating = requestDetails.Id;

  let defaultIntentionConfig = {
    placeholder: "",
    rows: 8,
    cols: 32,
    max: 1000,
  };

  let prices = currentProductState.DonationAmounts.split("|");

  // init state for form fields
  const [intentionConfig, setIntentionConfig] = useState(
    defaultIntentionConfig
  );

  const formRef = useRef(null);
  const intentionRef = useRef(null);

  const validationRules = {
    intention: Yup.string().required("Required"),
    cardType: Yup.string().required("Required"),
    amount: Yup.string().required("Please select an amount"),
    customAmount: Yup.number()
      .typeError("Please enter a donation amount of at least $1.00.")
      .when("amount", (amount) => {
        if (amount == "other")
          return Yup.number()
            .typeError("Please enter a donation amount of at least $1.00.")
            .min(1, "Please enter a donation amount of at least $1.00.")
            .required("Please enter a donation amount of at least $1.00.");
      }),
    cardQuantity: Yup.number()
      .typeError("Please enter a card quantity.")
      .when("cardType", (cardType) => {
        if (cardType == "sendtome")
          return Yup.number()
            .typeError("Please enter a card quantity of at least 1.")
            .min(1, "Please enter a card quantity of at least 1.")
            .max(99, "You can only request a maximum of 99 cards.")
            .required("Please enter a card quantity of at least 1.");
      }),
  };

  let donationAmount = "";
  let donationCustomAmount = "";
  let defaultPriceFound = false;

  prices.forEach((price) => {
    if (Number(price) == Number(requestDetails.OfferingAmount)) {
      defaultPriceFound = true;
    }
  });

  if (defaultPriceFound) {
    donationAmount = String(Number(requestDetails.OfferingAmount).toFixed(2));
  } else {
    if (requestDetails.OfferingAmount) {
      donationAmount = "other";
      donationCustomAmount = String(
        Number(requestDetails.OfferingAmount).toFixed(2)
      );
    }
  }

  const formSchema = Yup.object().shape(validationRules);

  // add to cart if no card
  useEffect(() => {
    if (addingToCart) {
      let currentCart = { ...requestDetails };
      let currentPreview = { ...currentPreviewState };
      let currentProduct = { ...currentProductState };
      dispatch(sendCartData(currentCart, currentPreview, currentProduct));

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [dispatch, addingToCart]);

  function formSubmitHandler(values) {
    // if request is a no card, add to cart right now and move to last step
    console.log(values);
    dispatch(configuratorActions.setRequest(values));
    dispatch(cardPreviewActions.setFieldFocus(""));
    FormHelpers.scrollToTop();
  }

  const cardTypeClickHandler = (event) => {
    dispatch(configuratorActions.setCardType(event.target.value));

    let productType = "nocard";
    if (event.target.value == "sendtome") {
      productType = "cards";
    } else if (event.target.value == "sendtosomeone" || event.target.value == "ecard") {
      productType = "card";
      dispatch(configuratorActions.setCardQuantity(0));
      formRef.current.values.cardQuantity = 0;
    } else {
      dispatch(configuratorActions.setCardQuantity(0));
      formRef.current.values.cardQuantity = 0;
    }

    dispatch(
      cardPreviewActions.setSelectedOccasionState(
        currentRequest.Products[productType]
      )
    );
  };

  const inputFocusHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(e.target.name));
  };

  const inputBlurHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  return (
    <div className="cfg-input-form cfg-request" data-step="request">
      <Formik
        initialValues={{
          intention: requestDetails.Intention,
          cardType: requestDetails.CardTypeValue,
          amount: donationAmount,
          customAmount: donationCustomAmount,
          cardQuantity: requestDetails.CardQuantity,
        }}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
              <Row>
                <Col xs="12">
                  <InfoTooltip
                    id="intentionHelp"
                    title="Help"
                    text="The person(s) on whose behalf the Mass is to be celebrated.  The Mass may also be for a non-person, such as “World Peace”."
                  />
                  <h4 className="required">
                    Please list your names – separated by a comma.
                  </h4>
                  <FormGroup>
                    <Input
                      name="intention"
                      type="textarea"
                      id="intention"
                      ref={intentionRef}
                      datamax={intentionConfig.max}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      value={values.intention}
                      rows={intentionConfig.rows}
                      cols={intentionConfig.cols}
                      className="cfg-intention-input parse-new-lines"
                      placeholder={intentionConfig.placeholder}
                      onChange={(e) => {
                        handleChange(e);
                        dispatch(
                          cardPreviewActions.setFieldValue({
                            key: "CardIntention",
                            value: e.target.value,
                          })
                        );
                      }}
                      onFocus={(e) => {
                        inputFocusHandler(e);
                      }}
                      onBlur={(e) => {
                        FormHelpers.cleanupEmptyLines(e, values, 'intention');
                        handleBlur(e);
                        inputBlurHandler();
                      }}
                      invalid={Boolean(
                        touched["intention"] && errors["intention"]
                      )}
                    />
                    {touched["intention"] && errors["intention"] ? (
                      <FormFeedback>{errors["intention"]}</FormFeedback>
                    ) : (
                      ""
                    )}
                  </FormGroup>
                </Col>
                <Col xs="12">
                  <h4 className="required">Tell Us If You Need A Card:</h4>
                  <FormGroup>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="nocard"
                          onClick={cardTypeClickHandler}
                        />
                        No card required
                      </label>
                    </div>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="sendtome"
                          onClick={cardTypeClickHandler}
                        />
                        Yes, please send{" "}
                        <input
                          type="text"
                          name="cardQuantity"
                          value={
                            values.cardQuantity != 0 ? values.cardQuantity : ""
                          }
                          maxLength="2"
                          className="cardQty"
                          onChange={handleChange}
                          onBlur={(e) => {
                            values.cardType = "sendtome";
                            handleBlur(e);
                            if (!errors.cardQuantity) {
                              dispatch(
                                configuratorActions.setCardType("sendtome")
                              );
                            }
                          }}
                        />{" "}
                        blank cards to me.
                      </label>
                    </div>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="sendtosomeone"
                          onClick={cardTypeClickHandler}
                        />
                        Yes, send a card to someone else for me
                      </label>
                    </div>
                    <div
                      className={`form-check ${
                        errors.cardType && touched.cardType ? "is-invalid" : ""
                      }`}
                    >
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="ecard"
                          onClick={cardTypeClickHandler}
                        />
                        Send an eCard <em>for</em> me
                      </label>
                    </div>
                    {errors.cardType && touched.cardType && (
                      <div className="invalid-feedback">{errors.cardType}</div>
                    )}
                    {errors.cardQuantity && touched.cardQuantity && (
                      <div className="invalid-feedback">
                        {errors.cardQuantity}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs="12">
                  <div className="mb-3">
                    <h4 className="required">
                      My offering to support the Capuchin missionaries is:
                    </h4>

                    {prices.map((price) => (
                      <div className="form-check" key={price}>
                        <label>
                          <Field
                            type="radio"
                            name="amount"
                            value={price.toString()}
                            onChange={handleChange}
                          />
                          ${price}
                        </label>
                      </div>
                    ))}

                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="amount"
                          value="other"
                          onChange={handleChange}
                        />
                        Other
                      </label>
                    </div>

                    {errors.amount && touched.amount && (
                      <div className="invalid-feedback">{errors.amount}</div>
                    )}

                    {values.amount === "other" && (
                      <div>
                        <label>
                          $ <Field type="text" name="customAmount" />
                        </label>
                      </div>
                    )}

                    {errors.customAmount && touched.customAmount && (
                      <div className="invalid-feedback">
                        {errors.customAmount}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              {/* <DebugOutput
                errors={errors}
                values={FormHelpers.getValues(values)}
              /> */}
            </Container>
            <Button
              type="submit"
              size="sm"
              color="primary"
              className="float-end"
            >
              {requestDetails.CardTypeValue === "nocard" &&
                (isUpdating ? "Update Cart" : "Add to Cart")}

              {requestDetails.CardTypeValue !== "nocard" && "Next"}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default RequestForm;
